import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Gift,
  HandCoins,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  QrCode,
  Search,
  UserRoundCheck,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar, Brand, IconButton } from "../../design-system";

interface NavigationItem {
  to: string;
  end?: boolean;
  label: string;
  icon: typeof LayoutDashboard;
}

interface NavigationSection {
  title?: string;
  links: NavigationItem[];
}

const adminSections: NavigationSection[] = [
  {
    links: [
      { to: "/admin", end: true, label: "Resumo", icon: LayoutDashboard },
      { to: "/admin/leads", label: "Leads", icon: UserRoundCheck },
    ],
  },
  {
    title: "Gestão",
    links: [
      { to: "/admin/parceiros", label: "Indicadores", icon: UsersRound },
      { to: "/admin/pontos", label: "Pontuação", icon: HandCoins },
      { to: "/admin/recompensas", label: "Recompensas", icon: Gift },
    ],
  },
];

const partnerSections: NavigationSection[] = [
  {
    links: [
      {
        to: "/parceiro",
        end: true,
        label: "Resumo",
        icon: LayoutDashboard,
      },
      {
        to: "/parceiro/indicacoes",
        label: "Minhas indicações",
        icon: BarChart3,
      },
    ],
  },
  {
    title: "Crescimento",
    links: [
      { to: "/parceiro/divulgar", label: "Divulgar", icon: QrCode },
      { to: "/parceiro/recompensas", label: "Recompensas", icon: Gift },
    ],
  },
];

export function AppShell({ mode }: { mode: "admin" | "partner" }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const profileRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const sections = mode === "admin" ? adminSections : partnerSections;
  const links = useMemo(
    () => sections.flatMap((section) => section.links),
    [sections],
  );
  const filteredLinks = links.filter((link) =>
    link.label
      .toLocaleLowerCase("pt-BR")
      .includes(search.toLocaleLowerCase("pt-BR")),
  );
  const quickAction =
    mode === "admin" ? "/admin/parceiros?novo=1" : "/parceiro/divulgar";
  const quickLabel = mode === "admin" ? "Novo indicador" : "Nova indicação";

  useEffect(() => {
    function closeProfile(event: MouseEvent) {
      if (!profileRef.current?.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    }

    document.addEventListener("mousedown", closeProfile);
    return () => document.removeEventListener("mousedown", closeProfile);
  }, []);

  async function handleLogout() {
    await auth.logout();
    navigate(mode === "admin" ? "/admin/entrar" : "/parceiro/entrar", {
      replace: true,
    });
  }

  function goTo(to: string) {
    setCommandOpen(false);
    setSearch("");
    navigate(to);
  }

  return (
    <div className={`app-shell ${collapsed ? "app-shell--collapsed" : ""}`}>
      <a className="skip-link" href="#main-content">
        Pular para o conteúdo
      </a>

      <aside
        className={`sidebar ${open ? "sidebar--open" : ""} ${collapsed ? "sidebar--collapsed" : ""}`}
        aria-label="Navegação principal"
      >
        <div className="sidebar__clinic" aria-hidden="true" />

        <nav>
          {sections.map((section, index) => (
            <section className="sidebar__section" key={section.title ?? index}>
              {section.title && (
                <span className="sidebar__section-title">{section.title}</span>
              )}
              {section.links.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={collapsed ? label : undefined}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    isActive ? "nav-link nav-link--active" : "nav-link"
                  }
                >
                  <Icon size={19} aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </section>
          ))}
        </nav>

        <div className="sidebar__branding">
          <Brand link={false} />
        </div>
      </aside>

      {open && (
        <button
          className="sidebar-backdrop"
          onClick={() => setOpen(false)}
          aria-label="Fechar menu"
        />
      )}

      <section className="app-content">
        <header className="app-topbar">
          <div className="app-topbar__left">
            <IconButton
              className="app-topbar__menu"
              onClick={() => setOpen(true)}
              label="Abrir menu"
            >
              <Menu />
            </IconButton>
            <IconButton
              className="app-topbar__collapse"
              onClick={() => setCollapsed((value) => !value)}
              label={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </IconButton>
          </div>

          <button
            type="button"
            className="app-search-trigger"
            onClick={() => setCommandOpen(true)}
          >
            <Search size={17} />
            <span>Pesquisar...</span>
          </button>

          <div className="app-topbar__right">
            <IconButton
              className="app-topbar__quick"
              onClick={() => navigate(quickAction)}
              label={quickLabel}
            >
              <Plus />
            </IconButton>
            <span className="app-topbar__separator" />
            <div
              className={`app-topbar__profile-menu ${profileOpen ? "is-open" : ""}`}
              ref={profileRef}
            >
              <button
                type="button"
                className="app-topbar__profile"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((value) => !value)}
              >
                <Avatar name={auth.user?.name ?? "Usuário"} size="sm" />
                <div>
                  <strong>{auth.user?.name ?? "Usuário"}</strong>
                  <small>{auth.user?.email}</small>
                </div>
                <ChevronDown size={16} aria-hidden="true" />
              </button>
              {profileOpen && (
                <div className="app-topbar__profile-dropdown" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      void handleLogout();
                    }}
                  >
                    <LogOut size={16} />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main id="main-content">
          <Outlet />
        </main>
      </section>

      {commandOpen && (
        <div
          className="command-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCommandOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setCommandOpen(false);
          }}
        >
          <section
            className="command-palette"
            role="dialog"
            aria-modal="true"
            aria-labelledby="command-title"
          >
            <h2 id="command-title" className="sr-only">
              Navegar pelo sistema
            </h2>
            <div className="command-palette__search">
              <Search />
              <input
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Digite o nome de uma página..."
                aria-label="Pesquisar páginas"
              />
              <IconButton
                label="Fechar busca"
                size="sm"
                onClick={() => setCommandOpen(false)}
              >
                <X />
              </IconButton>
            </div>
            <div className="command-palette__results">
              <small>Páginas</small>
              {filteredLinks.length ? (
                filteredLinks.map(({ to, label, icon: Icon }) => (
                  <button type="button" key={to} onClick={() => goTo(to)}>
                    <Icon />
                    <span>{label}</span>
                    <ChevronRight />
                  </button>
                ))
              ) : (
                <p>Nenhuma página encontrada.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
